/* eslint-disable react/display-name */
import { Typography, TypographyProps } from '@mui/material';
import ReactMarkdown from 'react-markdown';
import React from 'react';
import { MoSpacing } from '../MoSpacing';
import { MoExternalLink } from '../MoExternalLink';
import throwWithoutBreaking from '../../utils/throwWithoutBreaking';

type ComponentProps = {
  p?: TypographyProps;
  h1?: TypographyProps;
  h2?: TypographyProps;
  h3?: TypographyProps;
  h4?: TypographyProps;
  h5?: TypographyProps;
  h6?: TypographyProps;
  blockquote?: TypographyProps;
  ul?: TypographyProps;
  ol?: TypographyProps;
  li?: React.HTMLAttributes<HTMLLIElement>;
};

export function Markdown({
  markdown,
  spacing = 2,
  componentProps = {},
  linkUtmParams,
}: {
  markdown: string;
  spacing?: number;
  componentProps?: ComponentProps;
  linkUtmParams?: Partial<{
    utm_source: string;
    utm_medium: string;
    utm_campaign: string;
    utm_content: string;
    utm_term: string;
  }>;
}) {
  return (
    <MoSpacing y={spacing}>
      <ReactMarkdown
        components={{
          p: ({ children }) => <Typography {...componentProps.p}>{children}</Typography>,
          a: ({ children, href }) => {
            if (!href) return <>{children}</>;

            try {
              // Use the current location as the base for relative URLs
              const url = new URL(href, window.location.origin);

              // Add UTM params for external links
              if (linkUtmParams) {
                if (linkUtmParams.utm_source)
                  url.searchParams.set('utm_source', linkUtmParams.utm_source);
                if (linkUtmParams.utm_medium)
                  url.searchParams.set('utm_medium', linkUtmParams.utm_medium);
                if (linkUtmParams.utm_campaign)
                  url.searchParams.set('utm_campaign', linkUtmParams.utm_campaign);
                if (linkUtmParams.utm_content)
                  url.searchParams.set('utm_content', linkUtmParams.utm_content);
                if (linkUtmParams.utm_term)
                  url.searchParams.set('utm_term', linkUtmParams.utm_term);
              }

              return (
                <MoExternalLink href={url.toString()} openInNewTab>
                  {children}
                </MoExternalLink>
              );
            } catch (error) {
              throwWithoutBreaking(error as Error);
              return <>{children}</>;
            }
          },
          h1: ({ children }) => (
            <Typography variant="h1" {...componentProps.h1}>
              {children}
            </Typography>
          ),
          h2: ({ children }) => (
            <Typography variant="h2" {...componentProps.h2}>
              {children}
            </Typography>
          ),
          h3: ({ children }) => (
            <Typography variant="h3" {...componentProps.h3}>
              {children}
            </Typography>
          ),
          h4: ({ children }) => (
            <Typography variant="h3" {...componentProps.h4}>
              {children}
            </Typography>
          ),
          h5: ({ children }) => (
            <Typography variant="h3" {...componentProps.h5}>
              {children}
            </Typography>
          ),
          h6: ({ children }) => (
            <Typography variant="h3" {...componentProps.h6}>
              {children}
            </Typography>
          ),
          blockquote: ({ children }) => (
            <Typography component="blockquote" {...componentProps.blockquote}>
              {children}
            </Typography>
          ),
          ul: ({ children }) => (
            <Typography component="ul" {...componentProps.ul}>
              {children}
            </Typography>
          ),
          ol: ({ children }) => (
            <Typography component="ol" {...componentProps.ol}>
              {children}
            </Typography>
          ),
          li: ({ children }) => <li {...componentProps.li}>{children}</li>,
        }}
      >
        {markdown}
      </ReactMarkdown>
    </MoSpacing>
  );
}
